import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CatalogEntry, CredentialProvider, SavedCredential } from '../../../api/admin';
import type {
  ConnectionConditionGroup, ConnectionRow, WorkflowGraph, WorkflowNode,
} from '../../../api/forms';
import type { FieldDefinition } from '../../../shared/types';
import type { SecInfo } from '../fieldGroups';
import { Modal, Toggle } from '../../../components/fs';
import { useUnsavedGuard } from '../../../components/unsaved';
import {
  ConnectionEditor, PickableForm, RunWhenEditor, ScopeInfo, answerOptions, answerText, describeGroup,
  incomingTokens,
} from '../ConnectionEditors';
import {
  MAX_BRANCH_PATHS, ancestorConnections, ancestorPath, ancestorsOf, descendants, extraPathsOf, nearestMint,
  nextPathPort, ordinalWord, sharingSteps,
} from './graph';
import { CARD, EXIT_W, manyPathsFact } from './WorkflowCanvas';
import CompletionFields, { CompletionValue, thankYouTokens } from '../CompletionFields';
import { ParentForm, parentFieldDefs, parentFileTokens, parentLoopsOf, parentOptGroups, parentTokenGroups } from '../parentFields';

/**
 * Editing one step, in the house modal — decision 3a.
 *
 * The body is the EXISTING editor for the connection's type. Nothing about
 * `EmailEditor`, `MondayEditor`, `SpecEditor`, `CredentialChoice` or
 * `TemplateInput` was rewritten to live here; a step is structure and a
 * connection is configuration, and this modal is only the seam between them.
 *
 * What the modal adds is the ancestry strip: the one fact a scrim hides is
 * "where does this step sit?", and it is also the reason the token picker
 * offers what it offers. A step may only name what its ancestors published, so
 * the strip and the picker are two views of one rule.
 *
 * This is the one place a step is edited, at every width (2026-10-08): R6's
 * side inspector showed the same bodies in a 360px column, and an email's
 * body could not be read there. A deliver step's dialog is the 860px one for
 * that reason, and carries the on/off and test-mode switches the inspector had.
 */

export interface NodeEditorProps {
  graph: WorkflowGraph;
  node: WorkflowNode;
  connections: ConnectionRow[];
  catalog: CatalogEntry[];
  fields: FieldDefinition[];
  widgetScopes: ScopeInfo[];
  /**
   * The form's sections and cards, so the value pickers below read like the
   * form does — the Logic page's arrangement. Two questions can share a label,
   * and mapping the wrong one of those to an integration column is a wrong
   * delivery nobody would spot.
   */
  sections?: SecInfo[];
  /**
   * The forms this one continues, for a child form. Their questions join
   * every picker as `parent.<key>` — a step can send the sales rep's email to
   * Monday and a branch can test the order type, neither of which the
   * customer was ever shown. Empty for a form that continues nothing.
   */
  parents?: ParentForm[];
  otherForms: PickableForm[];
  credProviders: CredentialProvider[];
  savedCreds: SavedCredential[];
  /** false for a Member: they may pick a saved credential, never manage one */
  canManageCredentials?: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Structural edits — a branch's condition and labels live on the node. */
  onPatchNode: (id: string, patch: Partial<WorkflowNode>, opts?: { coalesce?: string }) => void;
  /** Clicking an ancestor chip moves the modal to that step. */
  onGoTo: (id: string) => void;
  /** Flip `enabled` or `dry_run` on the step's connection — the ⋯ menu's flags. */
  onToggleConnection?: (id: string, patch: { enabled?: boolean; dry_run?: boolean }) => void;
}

/** The chip label for a step: the ancestry strip's words. */
export function chipLabelFor(
  n: WorkflowNode, connById: Map<number, ConnectionRow>, specOf: (type?: string) => CatalogEntry | undefined
): string {
  if (n.kind === 'start') return 'submit';
  if (n.kind === 'merge') return '↳ rejoin';
  if (n.kind === 'branch') return `? ${String(n.config?.name || 'branch')}`;
  if (n.kind === 'end') return `⚑ ${n.config?.mode === 'continue' ? 'continue' : String(n.config?.title || 'ending')}`;
  if (n.kind === 'chase') return `⏰ ${String(n.config?.name || 'reminders')}`;
  const c = n.connectionId ? connById.get(n.connectionId) : undefined;
  return `${specOf(c?.type)?.icon || '•'} ${c?.name || n.kind}`;
}

/** The chip path across the top: submit → … → this step. */
export function Ancestry({ graph, node, label, onGoTo }: {
  graph: WorkflowGraph;
  node: WorkflowNode;
  label: (n: WorkflowNode) => string;
  onGoTo: (id: string) => void;
}) {
  const path = ancestorPath(graph, node.id);
  return (
    <div className="wf-ancestry">
      <span className="label">Where it sits</span>
      {path.map((n) => (
        <span key={n.id} style={{ display: 'contents' }}>
          <button type="button" className="chip" onClick={() => onGoTo(n.id)}>{label(n)}</button>
          <span className="arrow">→</span>
        </span>
      ))}
      <span className="chip here">this step</span>
    </div>
  );
}

/**
 * Said at the top of a step whose settings another step also runs.
 *
 * Until 2026-10-07 "Add another like this" drew a second step on the same
 * connection, and a workflow drawn then still has them. Nothing told the
 * author that the two were one thing, so this does — and names the way out.
 */
export function SharedSettingsNote({ graph, node, label, onGoTo }: {
  graph: WorkflowGraph; node: WorkflowNode; label: (n: WorkflowNode) => string; onGoTo: (id: string) => void;
}) {
  const others = sharingSteps(graph, node.id);
  if (!others.length) return null;
  // Both steps carry the same name — it is the connection's — so each is
  // told apart by what it comes after, which is the one thing that differs.
  const whereOf = (n: WorkflowNode) => {
    const above = ancestorPath(graph, n.id);
    return above.length ? `the one after ${label(above[above.length - 1])}` : label(n);
  };
  return (
    <div className="fs-consequence warn" style={{ marginBottom: 12 }}>
      These settings are shared with {others.length === 1 ? 'another step' : `${others.length} other steps`}
      {' '}({others.map((n, i) => (
        <span key={n.id}>
          {i > 0 && ', '}
          <button type="button" className="wf-link" onClick={() => onGoTo(n.id)}>{whereOf(n)}</button>
        </span>
      ))}), so a change here changes {others.length === 1 ? 'both' : 'all of them'}. To change only this one, choose
      {' '}<b>Give this step its own settings</b> in its ⋯ menu first.
    </div>
  );
}

/** What a start or a rejoin has to say for itself — there is nothing to set. */
export function InfoBody({ node }: { node: WorkflowNode }) {
  const body = node.kind === 'merge'
    ? 'The paths above come back together here, and everything below runs once, whichever way the submission came.'
    : 'Every submission to this form starts here. There is nothing to configure — it is a fact, not a step.';
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{body}</p>
      {node.kind === 'merge' && (
        <div className="fs-consequence" style={{ marginTop: 12 }}>
          A step below a rejoin can only use what <b>every</b> path above supplies. Something only one
          path publishes comes out blank for the submissions that took the other one.
        </div>
      )}
    </>
  );
}

export default function NodeEditorModal(props: NodeEditorProps) {
  const {
    graph, node, connections, catalog, onClose, onGoTo,
  } = props;

  const connById = new Map(connections.map((c) => [c.id, c]));
  const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
  const specOf = (type?: string) => catalog.find((c) => c.type === type);
  const label = (n: WorkflowNode): string => chipLabelFor(n, connById, specOf);

  if (node.kind === 'branch') {
    return <BranchEditor {...props} ancestry={
      <Ancestry graph={graph} node={node} label={label} onGoTo={onGoTo} />
    } />;
  }

  if (node.kind === 'end') {
    return <EndingEditor {...props} ancestry={
      <Ancestry graph={graph} node={node} label={label} onGoTo={onGoTo} />
    } />;
  }

  if (node.kind === 'chase') {
    return <ChaseEditor {...props} ancestry={
      <Ancestry graph={graph} node={node} label={label} onGoTo={onGoTo} />
    } />;
  }

  if (node.kind === 'merge' || node.kind === 'start') {
    return (
      <Modal title={label(node) || node.kind} onClose={onClose}
        footer={<button className="fs-btn neutral" onClick={onClose}>Close</button>}>
        {node.kind !== 'start' && <Ancestry graph={graph} node={node} label={label} onGoTo={onGoTo} />}
        <InfoBody node={node} />
      </Modal>
    );
  }

  // ---- deliver and mint: the existing editor, in the modal ----------------
  if (!conn) {
    return (
      <Modal title="This step has nothing to run" onClose={onClose}
        footer={<button className="fs-btn neutral" onClick={onClose}>Close</button>}>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          The connection this step pointed at is no longer on this form. Delete the step, or draw a new one.
        </p>
      </Modal>
    );
  }

  // Ancestors only. Passing these as the editor's `siblings` is what scopes its
  // token picker to what runs ABOVE this step — the same rule the strip draws.
  const upstreamIds = ancestorConnections(graph, node.id);
  const upstream = connections.filter((c) => upstreamIds.has(c.id));

  return (
    <ConnectionModal
      {...props} conn={conn} upstream={upstream} label={label}
      consumerName={consumerOf(graph, node, conn, connections, catalog)}
    />
  );
}

/**
 * The nearest step below this one that actually mentions something this step
 * publishes, by name.
 *
 * "Nearest" is by distance down the drawing, not by list order: the point is
 * to name the step a reader would look for next. A step whose config quotes
 * `{{inviteUrl}}` is using the invite whether or not anybody remembers wiring
 * it, which is precisely why naming it is worth doing — the alternative is an
 * author who cannot tell a live handoff from a dead one.
 */
export function consumerOf(
  graph: WorkflowGraph, node: WorkflowNode, conn: ConnectionRow,
  connections: ConnectionRow[], catalog: CatalogEntry[]
): string | null {
  const provides = catalog.find((c) => c.type === conn.type)?.provides || [];
  if (!provides.length) return null;
  const tokens = provides.map((p) => tokenNameOf(conn.type, p.key));
  const below = descendants(graph, node.id);
  const byId = new Map(connections.map((c) => [c.id, c]));
  // Breadth-first from this node, so the first match found is the closest one.
  const queue = [node.id];
  const seen = new Set([node.id]);
  while (queue.length) {
    const at = queue.shift()!;
    for (const e of graph.edges) {
      if (e.from !== at || seen.has(e.to)) continue;
      seen.add(e.to);
      queue.push(e.to);
      if (!below.has(e.to)) continue;
      const next = graph.nodes.find((n) => n.id === e.to);
      const c = next?.connectionId ? byId.get(next.connectionId) : null;
      if (!c) continue;
      // Whitespace inside the braces is normalised away first, so `{{ inviteUrl }}`
      // counts as the same mention as `{{inviteUrl}}` — which is the same
      // latitude the renderer gives them.
      const blob = JSON.stringify(c.config ?? {}).replace(/\{\{\s*([\w.]+)\s*\}\}/g, '{{$1}}');
      if (tokens.some((t) => blob.includes(`{{${t}}}`))) return c.name;
    }
  }
  return null;
}

/** `invite` + `url` -> `inviteUrl` — the server's rule, mirrored. */
const tokenNameOf = (type: string, key: string) =>
  type.replace(/[^a-zA-Z0-9]/g, '') + key.charAt(0).toUpperCase() + key.slice(1);

function ConnectionModal({
  graph, node, conn, upstream, catalog, fields, widgetScopes, sections, parents, otherForms,
  credProviders, savedCreds, canManageCredentials, onClose, onSaved, onGoTo, onToggleConnection, label,
  consumerName,
}: NodeEditorProps & {
  conn: ConnectionRow;
  upstream: ConnectionRow[];
  label: (n: WorkflowNode) => string;
  consumerName: string | null;
}) {
  const save = useRef<(() => Promise<void>) | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const spec = catalog.find((c) => c.type === conn.type);

  async function commit() {
    if (!save.current) return;
    setSaving(true);
    try { await save.current(); } finally { setSaving(false); }
  }

  /**
   * The delivery settings are the one thing in this modal that does not save
   * as it is drawn — the canvas around it autosaves, this does not — so
   * Escape, the scrim and Cancel all ask before throwing an edit away.
   */
  const guard = useUnsavedGuard({
    dirty, busy: saving,
    what: `Your changes to ${conn.name}`,
    onSave: () => commit(),
  });
  const closeStep = () => guard.confirm(onClose);
  const parentGroups = useMemo(() => parentTokenGroups(parents || []), [parents]);
  const parentLoops = useMemo(() => parentLoopsOf(parents || []), [parents]);
  const parentFiles = useMemo(() => parentFileTokens(parents || []), [parents]);

  return (
    <Modal
      xl
      title={`${spec?.title || conn.type} — ${conn.name}`}
      aside="a step on this form's workflow"
      onClose={closeStep}
      footer={
        <>
          <button className="fs-btn primary" disabled={saving} onClick={commit}>
            {saving ? 'Saving…' : 'Save step'}
          </button>
          <button className="fs-btn neutral" onClick={closeStep}>Cancel</button>
          <span style={{ flex: 1 }} />
          <span>Saved into the draft — publish to send it</span>
        </>
      }
    >
      {/* An unsaved edit is asked about before the dialog moves to another step. */}
      <Ancestry graph={graph} node={node} label={label} onGoTo={(id) => guard.confirm(() => onGoTo(id))} />
      {onToggleConnection && (
        <div className="wf-step-switches">
          <Toggle
            checked={conn.enabled}
            onChange={(on) => onToggleConnection(node.id, { enabled: on })}
            label={conn.enabled ? 'Switched on' : 'Switched off — this step is skipped'}
          />
          <Toggle
            checked={!!conn.dry_run}
            onChange={(on) => onToggleConnection(node.id, { dry_run: on })}
            label={conn.dry_run ? 'Test mode — built, nothing is sent' : 'Test mode'}
          />
        </div>
      )}
      <SharedSettingsNote graph={graph} node={node} label={label} onGoTo={(id) => guard.confirm(() => onGoTo(id))} />
      <ConnectionEditor
        conn={conn}
        spec={spec}
        catalog={catalog}
        // Ancestors, not every connection on the form — constraint 4.
        siblings={upstream}
        fields={fields}
        widgetScopes={widgetScopes}
        sections={sections}
        parentGroups={parentGroups.length ? parentGroups : undefined}
        parentLoops={parentLoops}
        parentFiles={parentFiles.length ? parentFiles : undefined}
        otherForms={otherForms}
        credProviders={credProviders}
        savedCreds={savedCreds}
        canManageCredentials={canManageCredentials}
        // Routing is drawn now. A condition still ON the connection keeps its
        // editor, because the runner still honours it and somebody has to be
        // able to see the thing quietly deciding whether this step happens.
        hideRunWhen={!conn.run_when?.when?.length}
        consumerName={consumerName}
        bindSave={(fn) => { save.current = fn; }}
        onDirtyChange={setDirty}
        onSaved={() => { onSaved(); onClose(); }}
      />
      {/*
        No "Only run when… has moved to the canvas" note.

        It explained an absence, at the end of every step editor, to somebody
        who had already found what they came for — and an explanation of where
        a feature USED to be is the shortest-lived sentence in a product. The
        canvas above says where this step sits; that is the whole answer, and
        the ancestry strip is already saying it at the top of this modal.
      */}
      {guard.node}
    </Modal>
  );
}

// ---- the branch -------------------------------------------------------------

/** One path after the first, while it is being edited. */
export interface BranchPathValue {
  port: string;
  when: ConnectionConditionGroup | null;
  label: string;
}

/** What the branch editor holds while it is being typed into. */
export interface BranchValue {
  name: string;
  when: ConnectionConditionGroup | null;
  first: string;
  second: string;
  /**
   * The paths between the first and "everything else", in the order they are
   * asked — a path per answer, where a dropdown has more than two (2026-09-27).
   */
  more: BranchPathValue[];
}

export function branchValueOf(node: WorkflowNode): BranchValue {
  const cfg = node.config || {};
  const labels = (cfg.labels as string[]) || [];
  return {
    name: String(cfg.name || ''),
    when: (cfg.when as ConnectionConditionGroup) || null,
    first: labels[0] || '',
    second: labels[1] || '',
    more: extraPathsOf(node).map((p) => ({
      port: p.port, when: (p.when as ConnectionConditionGroup) || null, label: p.label,
    })),
  };
}

/**
 * The answer's own words, so the exits are never yes/no even untouched.
 *
 * "Own words" means the words the form shows: a dropdown stores
 * `dropShipPlusInstall` and shows "Drop Ship + Installation", and the exit
 * on the card is read by whoever is looking at the drawing, not by the
 * runner.
 */
function suggestedLabel(when: ConnectionConditionGroup | null, describable: FieldDefinition[]): string {
  const row = when?.when?.[0];
  return answerText(describable.find((f) => f.key === row?.field), row?.value);
}

export function branchLabels(v: BranchValue, describable: FieldDefinition[]): [string, string] {
  return [v.first || suggestedLabel(v.when, describable) || 'the first path', v.second || 'everything else'];
}

/** A further path's words: typed, else the answer it picks, else its place. */
export function pathLabel(p: BranchPathValue, i: number, describable: FieldDefinition[]): string {
  return p.label || suggestedLabel(p.when, describable) || `the ${ordinalWord(i + 2)} path`;
}

/** Rows that name a question, and nothing else — no rows is no condition. */
function conditionOf(g: ConnectionConditionGroup | null): Record<string, unknown> | null {
  const rows = (g?.when || []).filter((r) => r.field);
  return rows.length ? { match: g?.match === 'any' ? 'any' : 'all', when: rows } : null;
}

/**
 * The node config a branch value stores: rows with a question only, and the
 * labels. The further paths are written whole, and not at all when there are
 * none — so a two-way branch stores exactly what it always did.
 */
export function branchConfigOf(
  node: WorkflowNode, v: BranchValue, describable: FieldDefinition[]
): Record<string, unknown> {
  const { extraPaths: _replaced, ...rest } = node.config || {};
  return {
    ...rest,
    name: v.name.trim(),
    when: conditionOf(v.when),
    labels: branchLabels(v, describable),
    ...(v.more.length ? {
      extraPaths: v.more.map((p, i) => ({ port: p.port, when: conditionOf(p.when), label: pathLabel(p, i, describable) })),
    } : {}),
  };
}

/**
 * The branch's fields: the Logic page's own condition rows, plus the labels.
 *
 * The labels are the new work and they matter more than they look. The exits
 * are never yes/no — they carry the author's own words, defaulting to the
 * answer that was picked, so the drawing reads as a sentence about the form
 * rather than as a truth table.
 *
 * A branch can hold more than two paths (2026-09-27): each further one is its
 * own condition rows, asked in order after the first, and "a path for each
 * other answer" writes one per answer the first path's question has left —
 * which is the whole of routing a four-answer dropdown, in one click.
 */
export function BranchBody({ value, onChange, fields, sections, parents, reserved = [] }: {
  value: BranchValue;
  onChange: (next: BranchValue) => void;
  fields: FieldDefinition[];
  sections?: SecInfo[];
  parents?: ParentForm[];
  /** Ports an arrow still leaves from, never given to a new path — see `nextPathPort`. */
  reserved?: string[];
}) {
  // The order's questions, for a child form: offered after this form's own,
  // and known by name when the card describes the condition back.
  const parentGroups = useMemo(() => parentOptGroups(parents || []), [parents]);
  const parentDefs = useMemo(() => parentFieldDefs(parents || []), [parents]);
  const describable = useMemo(() => [...fields, ...parentDefs], [fields, parentDefs]);
  const [firstLabel, secondLabel] = branchLabels(value, describable);
  const suggested = suggestedLabel(value.when, describable);
  const set = (patch: Partial<BranchValue>) => onChange({ ...value, ...patch });
  const { more } = value;
  const many = more.length > 0;
  const setPath = (i: number, patch: Partial<BranchPathValue>) =>
    set({ more: more.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const room = MAX_BRANCH_PATHS - (more.length + 2);

  // What "a path for each other answer" splits on: the first path's question,
  // when it is one whose answers are picked from a list — and which of those
  // answers no path names yet. Offered only when "everything else" is lumping
  // two or more of them together: a two-answer question is already split, and
  // a path for its last answer would only take those submissions away from the
  // path that handles them now.
  const firstRow = value.when?.when?.[0];
  const asked = describable.find((f) => f.key === firstRow?.field);
  const answers = answerOptions(asked) || [];
  const pickOp = firstRow?.op === 'includes' || asked?.input_type === 'multiselect' ? 'includes' : 'is';
  const named = new Set([value.when, ...more.map((p) => p.when)].flatMap((g) => (g?.when || [])
    .filter((r) => r.field === firstRow?.field && (r.op === 'is' || r.op === 'includes'))
    .map((r) => r.value)));
  const unnamed = answers.filter((o) => !named.has(o.value));
  // Which answers the last path catches, where that can be said exactly: every
  // path picks answers of that one question (`is`, or `includes` on a list,
  // one row or any of several), so an answer no path names goes to the last.
  // It is what makes "this answer, or all the others" — two paths — read as
  // the whole choice it is, with no path per answer needed.
  const exact = named.size > 0 && [value.when, ...more.map((p) => p.when)].every((g) => {
    const rows = (g?.when || []).filter((r) => r.field);
    return rows.every((r) => r.field === firstRow?.field && (r.op === 'is' || r.op === 'includes'))
      && (rows.length <= 1 || g?.match === 'any');
  });
  const caught = exact ? unnamed.map((o) => o.text) : [];
  const caughtWords = caught.length > 6
    ? `${caught.slice(0, 5).join(', ')} or ${caught.length - 5} more`
    : caught.length > 1 ? `${caught.slice(0, -1).join(', ')} or ${caught[caught.length - 1]}` : caught[0];

  /** New paths, one per answer given (or one blank one), after the last. */
  function addPaths(answersToAdd: (string | null)[]) {
    const taken = ['yes', 'no', ...more.map((p) => p.port), ...reserved];
    const added: BranchPathValue[] = [];
    for (const answer of answersToAdd.slice(0, Math.max(0, room))) {
      const port = nextPathPort([...taken, ...added.map((p) => p.port)]);
      added.push({
        port,
        when: answer !== null && firstRow?.field
          ? { match: 'all', when: [{ field: firstRow.field, op: pickOp, value: answer }] }
          : null,
        label: '',
      });
    }
    // The last path was often labelled with the one answer it caught ("the
    // rep"). An answer with a path of its own is not caught there any more, so
    // its name goes back to "everything else" rather than labelling two exits.
    const nowOwnPath = new Set(added.map((p) => answerText(asked, p.when?.when?.[0]?.value)).filter(Boolean));
    set({ more: [...more, ...added], ...(nowOwnPath.has(value.second.trim()) ? { second: '' } : {}) });
  }

  const exits = [firstLabel, ...more.map((p, i) => pathLabel(p, i, describable)), secondLabel];

  return (
    <>
      <div className="ad-field ne-w-320" style={{ maxWidth: 320 }}>
        <label>Branch name</label>
        <input value={value.name} onChange={(e) => set({ name: e.target.value })} placeholder="Who fills it in?" />
        <small>Shown on the card — a question reads best.</small>
      </div>

      <div style={{ marginTop: 4 }}>
        <RunWhenEditor
          value={value.when} fields={fields} sections={sections} onChange={(when) => set({ when })}
          extraGroups={parentGroups.length ? parentGroups : undefined}
          extraFields={parentDefs.length ? parentDefs : undefined}
          label="Take the first path when…"
          emptyHint={many
            ? 'No condition yet — until there is one, no submission takes this path.'
            : 'No condition yet — until there is one, every submission takes the second path.'}
        />
      </div>
      {more.map((p, i) => (
        <div key={p.port} className="wf-branch-path">
          <RunWhenEditor
            value={p.when} fields={fields} sections={sections} onChange={(when) => setPath(i, { when })}
            extraGroups={parentGroups.length ? parentGroups : undefined}
            extraFields={parentDefs.length ? parentDefs : undefined}
            label={`Otherwise, take the ${ordinalWord(i + 2)} path when…`}
            emptyHint="No condition yet — until there is one, no submission takes this path."
          />
          <button
            type="button" className="fs-btn text danger sm"
            onClick={() => set({ more: more.filter((_, j) => j !== i) })}
          >Remove the {ordinalWord(i + 2)} path</button>
        </div>
      ))}
      <small style={{ display: 'block', color: 'var(--text-muted)', marginTop: -4 }}>
        {many && 'A submission takes the first path whose condition holds, top to bottom. '}
        {caughtWords
          ? `Every other submission takes the ${many ? 'last' : 'second'} path — that is ${caughtWords}. `
            + 'A branch never swallows anybody.'
          : `Every other submission takes the ${many ? 'last' : 'second'} path — a branch never swallows anybody.`}
      </small>
      <div className="wf-branch-add">
        <button
          type="button" className="fs-btn neutral sm" disabled={room <= 0}
          onClick={() => addPaths([unnamed[0]?.value ?? null])}
        >+ Add another path</button>
        {unnamed.length > 1 && room > 0 && (
          <button type="button" className="fs-btn neutral sm" onClick={() => addPaths(unnamed.map((o) => o.value))}>
            + A path for each other answer ({Math.min(unnamed.length, room)})
          </button>
        )}
        {room <= 0 && <small>A branch holds at most {MAX_BRANCH_PATHS} paths.</small>}
      </div>

      <div className="fs-row-2" style={{ marginTop: 14 }}>
        <div className="ad-field">
          <label>Label the first path</label>
          <input value={value.first} onChange={(e) => set({ first: e.target.value })} placeholder={suggested || 'the first path'} />
        </div>
        {more.map((p, i) => (
          <div key={p.port} className="ad-field">
            <label>Label the {ordinalWord(i + 2)} path</label>
            <input
              value={p.label} onChange={(e) => setPath(i, { label: e.target.value })}
              placeholder={suggestedLabel(p.when, describable) || `the ${ordinalWord(i + 2)} path`}
            />
          </div>
        ))}
        <div className="ad-field">
          <label>{many ? 'Label the last path' : 'Label the other path'}</label>
          <input value={value.second} onChange={(e) => set({ second: e.target.value })} placeholder="everything else" />
        </div>
      </div>

      <div className="wf-preview-card" style={{ marginTop: 14 }}>
        <div className="cap">How the card will read</div>
        <div
          className="wf-node routing"
          style={{ position: 'relative', width: many ? Math.max(CARD, exits.length * EXIT_W) : 240, maxWidth: '100%' }}
        >
          <div className="card">
            <div className="wf-body">
              <span className="wf-tile routing">?</span>
              <span className="wf-text">
                <span className="wf-over routing">Branch on an answer</span>
                <span className="wf-name">{value.name || 'Which way?'}</span>
                {many ? (
                  <span className="wf-fact">
                    {manyPathsFact(
                      [value.when, ...more.map((p) => p.when)],
                      (key) => describable.find((f) => f.key === key)?.label || key
                    )}
                  </span>
                ) : !!value.when?.when?.length && (
                  <span className="wf-fact">{describeGroup(value.when, describable)}</span>
                )}
              </span>
            </div>
            {many ? (
              <div className="wf-exits many" style={{ gridTemplateColumns: `repeat(${exits.length}, minmax(0, 1fr))` }}>
                {exits.map((text, i) => <span key={i} title={text}>{text}</span>)}
              </div>
            ) : (
              <div className="wf-exits"><span>{firstLabel}</span><span>{secondLabel}</span></div>
            )}
          </div>
        </div>
        <small style={{ display: 'block', color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
          Labels default to the picked answer's own words, so the exits are never yes/no
          even before anybody types.
        </small>
      </div>
    </>
  );
}

/** The ports arrows leave this node by — what a new path must not be called. */
export const portsInUse = (graph: WorkflowGraph, node: WorkflowNode): string[] =>
  graph.edges.filter((e) => e.from === node.id).map((e) => e.port);

function BranchEditor({
  graph, node, fields, sections, parents, onClose, onPatchNode, ancestry,
}: NodeEditorProps & { ancestry: JSX.Element }) {
  const [value, setValue] = useState<BranchValue>(() => branchValueOf(node));
  const describable = useMemo(() => [...fields, ...parentFieldDefs(parents || [])], [fields, parents]);

  function commit() {
    onPatchNode(node.id, { config: branchConfigOf(node, value, describable) });
    onClose();
  }

  return (
    <Modal
      title={`Branch — ${value.name || 'which way?'}`}
      onClose={onClose}
      footer={
        <>
          <button className="fs-btn primary" onClick={commit}>Save branch</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
          <span style={{ flex: 1 }} />
          <span>Saved into the draft — publish to make it live</span>
        </>
      }
    >
      {ancestry}
      <BranchBody
        value={value} onChange={setValue} fields={fields} sections={sections} parents={parents}
        reserved={portsInUse(graph, node)}
      />
    </Modal>
  );
}

// ---- the ending -------------------------------------------------------------

/** "Continue into the follow-up form" with nothing above it that makes a link. */
export function endingBroken(graph: WorkflowGraph, node: WorkflowNode, value: CompletionValue): boolean {
  if (value.mode !== 'continue') return false;
  const above = ancestorsOf(graph, node.id);
  return !graph.nodes.some((n) => n.kind === 'mint' && above.has(n.id));
}

/**
 * An ending: what the person who submitted meets, on THIS path.
 *
 * The body is the After-submitting block from Settings, unchanged — the same
 * component, so the two can never drift into two answers to one question. What
 * an ending adds is the third kind, "continue into the follow-up form", which
 * only means anything where a path can be drawn to reach it.
 */
export function EndingBody({ graph, node, value, onChange, fields, sections, connections, catalog }: {
  graph: WorkflowGraph;
  node: WorkflowNode;
  value: CompletionValue;
  onChange: (next: CompletionValue) => void;
  fields: FieldDefinition[];
  sections?: SecInfo[];
  connections: ConnectionRow[];
  catalog: CatalogEntry[];
}) {
  // The flagship error, checked here as well as on the canvas so the editor
  // can refuse rather than let somebody save a Continue button with no link
  // behind it and find out from a customer days later.
  const broken = endingBroken(graph, node, value);
  // What the pickers offer: the answers, and what the steps on EVERY path to
  // this ending hand on — a value only one arm makes would come out blank for
  // the submissions that took the other.
  const tokens = useMemo(() => {
    const upstreamIds = ancestorConnections(graph, node.id);
    const upstream = connections.filter((c) => upstreamIds.has(c.id));
    return thankYouTokens(fields, sections, incomingTokens(upstream, catalog, '', 0));
  }, [graph, node.id, connections, catalog, fields, sections]);
  return (
    <>
      <CompletionFields value={value} onChange={onChange} allowContinue idPrefix={`end-${node.id}`} tokens={tokens} />
      {broken && (
        <div className="ad-err" style={{ marginTop: 12 }}>
          No step above this one makes a follow-up link, so there would be nothing to continue into.
          Add a link step on this path first.
        </div>
      )}
      <div className="fs-consequence" style={{ marginTop: 14 }}>
        Only the submissions that reach <b>this</b> ending see it. A path that reaches none falls
        back to the form's own <Link to="../settings">After-submitting</Link> setting.
      </div>
    </>
  );
}

function EndingEditor({
  graph, node, onClose, onPatchNode, ancestry, fields, sections, connections, catalog,
}: NodeEditorProps & { ancestry: JSX.Element }) {
  const [value, setValue] = useState<CompletionValue>({ ...(node.config || {}) } as CompletionValue);
  const broken = endingBroken(graph, node, value);

  return (
    <Modal
      wide
      title="Ending"
      aside="what this path finishes with"
      onClose={onClose}
      footer={
        <>
          <button
            className="fs-btn primary" disabled={broken}
            onClick={() => { onPatchNode(node.id, { config: { ...value } }); onClose(); }}
          >Save ending</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
          <span style={{ flex: 1 }} />
          <span>Saved into the draft — publish to send it</span>
        </>
      }
    >
      {ancestry}
      <EndingBody
        graph={graph} node={node} value={value} onChange={setValue}
        fields={fields} sections={sections} connections={connections} catalog={catalog}
      />
    </Modal>
  );
}

// ---- reminders --------------------------------------------------------------

/** "3, 7" → [3, 7]: whole days from 1 to 365, deduplicated, in order. */
export function parseChaseDays(text: string): number[] {
  const parsed = text.split(/[,\s]+/).map((d) => Math.floor(Number(d)))
    .filter((d) => Number.isFinite(d) && d > 0 && d <= 365);
  return [...new Set(parsed)].sort((a, b) => a - b);
}

/**
 * Reminders: how long to keep chasing somebody who has not filled it in.
 *
 * The node re-runs the path from the nearest link step above it, so what it
 * sends is whatever that path already sends — this editor only decides WHEN.
 * "Day 3 and day 7" used to be a constant in the sweep job; it is a setting
 * the moment reminders become something you can draw.
 */
export function ChaseBody({ graph, node, connections, days, onChange }: {
  graph: WorkflowGraph;
  node: WorkflowNode;
  connections: ConnectionRow[];
  /** the box's text, as typed */
  days: string;
  onChange: (text: string) => void;
}) {
  const mint = nearestMint(graph, node.id);
  // A mint's name is its CONNECTION's name — the node holds structure, the
  // connection holds everything a person wrote.
  const mintName = mint
    ? connections.find((c) => c.id === mint.connectionId)?.name || 'the link step above'
    : null;
  const clean = parseChaseDays(days);
  return (
    <>
      {mint ? (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginTop: 0 }}>
          Re-sends the link from <b>{mintName}</b> by running
          the steps between it and here again. The link itself is never re-made — two working links for
          one submission is a support call. A helpdesk step (Freshdesk, Zendesk) replies into the ticket
          it opened the first time rather than opening another; an email step simply sends again.
        </p>
      ) : (
        <div className="ad-err">
          No step above this one makes a follow-up link, so there is nothing to re-send.
          Put a link step on this path first.
        </div>
      )}
      <div className="ad-field ne-w-260" style={{ maxWidth: 260, marginTop: 12 }}>
        <label>Chase on days</label>
        <input value={days} onChange={(e) => onChange(e.target.value)} placeholder="3, 7" />
        <small>
          Days after the link went out. {clean.length
            ? `Chases ${clean.length === 1 ? 'once' : `${clean.length} times`}, then stops.`
            : 'At least one day is needed.'}
        </small>
      </div>
    </>
  );
}

function ChaseEditor({
  graph, node, connections, onClose, onPatchNode, ancestry,
}: NodeEditorProps & { ancestry: JSX.Element }) {
  const current = Array.isArray(node.config?.days) ? (node.config!.days as number[]) : [3, 7];
  const [days, setDays] = useState(current.join(', '));
  const clean = parseChaseDays(days);

  return (
    <Modal
      title="Reminders"
      aside="chasing a follow-up nobody has filled in"
      onClose={onClose}
      footer={
        <>
          <button
            className="fs-btn primary" disabled={!clean.length}
            onClick={() => { onPatchNode(node.id, { config: { ...(node.config || {}), days: clean } }); onClose(); }}
          >Save reminders</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
          <span style={{ flex: 1 }} />
          <span>Saved into the draft — publish to make it live</span>
        </>
      }
    >
      {ancestry}
      <ChaseBody graph={graph} node={node} connections={connections} days={days} onChange={setDays} />
    </Modal>
  );
}
